import { useEffect, useState, useCallback } from "react";
import MetaTags from "react-meta-tags";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useDispatch } from "react-redux";
import jwt_decode from "jwt-decode";
import FeatherIcon from "feather-icons-react";
import Select from "react-select";
import { Form, FormGroup, Input, Label } from "reactstrap";
import { setLoading } from "../../../store/actions";
import { getPropertyList } from "../../Inventory/pages/FacilityInventory/Helpers/Api";
import {
  getInvoices,
  getInvoiceCategories,
  getInvoiceVendors,
  getInvoiceStatusCounts,
  getInvoiceEligibility,
  formatCurrency,
} from "./Components/Api";
import { InvoicesTable } from "./Components/InvoiceTable";
import { InvoiceReviewQueue } from "./Components/InvoiceReviewQueue";
import { InvoiceCategoriesTable } from "./Components/InvoiceCategoryTable";
import { InvoiceVendorsTable } from "./Components/InvoiceVendorTable";
import { InvoiceAuditorsTable } from "./Components/InvoiceAuditorTable";
import { InvoicePaymentSettings } from "./Components/InvoicePaymentSettings";
import "./Components/invoice.scss";

const TABS = {
  INVOICES: "invoices",
  REVIEW: "review",
  CATEGORIES: "categories",
  VENDORS: "vendors",
  AUDITORS: "auditors",
  SETTINGS: "settings",
};

const INVOICE_STATUS_BUTTONS = [
  { value: "all", label: "All", icon: "list", color: "#475569" },
  { value: "Pending", label: "Pending", icon: "clock", color: "#D97706" },
  { value: "Approved", label: "Approved", icon: "check", color: "#4F46E5" },
  { value: "Paid", label: "Paid", icon: "check-circle", color: "#059669" },
  { value: "Declined", label: "Declined", icon: "x-circle", color: "#DC2626" },
];

const MONTH_OPTIONS = [
  { value: "", label: "All Months" },
  ...["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map(
    (m, i) => ({ value: String(i + 1), label: m })
  ),
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [
  { value: "", label: "All Years" },
  ...Array.from({ length: 6 }, (_, i) => {
    const y = currentYear - i;
    return { value: String(y), label: String(y) };
  }),
];

const selectStyles = {
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    borderColor: isFocused ? "#4F46E5" : "#E2E8F0",
    borderRadius: 8,
    minHeight: 34,
    fontSize: "0.80rem",
    boxShadow: isFocused ? "0 0 0 3px rgba(79,70,229,0.12)" : "none",
    minWidth: 140,
    backgroundColor: isDisabled ? "#f8f9fa" : base.backgroundColor,
    cursor: isDisabled ? "not-allowed" : "default",
    "&:hover": { borderColor: "#4F46E5" },
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    fontSize: "0.80rem",
    background: isSelected ? "#4F46E5" : isFocused ? "#EEF2FF" : "white",
    color: isSelected ? "white" : "#0F172A",
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: "1px solid #E2E8F0", zIndex: 999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const emptyFilters = {
  propertyId: "",
  buildingName: "",
  year: "",
  month: "",
  status: "all",
  categoryId: "",
  vendorName: "",
  entered: "",
  referenceNo: "",
  unitNo: "",
};

const ENTERED_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Entered" },
  { value: "false", label: "Not entered" },
];

const Invoice = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(TABS.INVOICES);
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [filters, setFilters] = useState({ ...emptyFilters });
  const [statusData, setStatusData] = useState({ counts: {}, totals: {} });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [refNoDraft, setRefNoDraft] = useState("");
  // Our own RD number. Not a server filter — see rdDigits below.
  const [rdNoDraft, setRdNoDraft] = useState("");

  // Debounce the Vendor Ref text filter so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) =>
        f.referenceNo === refNoDraft.trim() ? f : { ...f, referenceNo: refNoDraft.trim() }
      );
    }, 400);
    return () => clearTimeout(t);
  }, [refNoDraft]);

  // Unit No. is debounced the same way; the server matches it as a prefix (10 → 101, 102).
  const [unitNoDraft, setUnitNoDraft] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.unitNo === unitNoDraft.trim() ? f : { ...f, unitNo: unitNoDraft.trim() }));
    }, 400);
    return () => clearTimeout(t);
  }, [unitNoDraft]);

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem("accessToken");
      return token ? jwt_decode(token) : null;
    } catch { return null; }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    setUser(currentUser);
    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || "", currentUser?.id || "");
      setProperties(result?.propertyList || []);
    } catch (err) {
      console.error("Failed to fetch properties:", err);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getUserFromToken]);

  const fetchCategories = useCallback(async () => {
    const u = getUserFromToken();
    if ((u?.role || "").toLowerCase() === "vendor") { setCategoryOptions([]); return; }
    try {
      const res = await getInvoiceCategories({ activeOnly: "true" });
      setCategoryOptions(
        (res?.data || [])
          .map((c) => ({ value: c._id, label: c.name }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch (err) {
      console.error("Failed to fetch invoice categories:", err);
    }
  }, [getUserFromToken]);

  const fetchVendors = useCallback(async () => {
    const u = getUserFromToken();
    if ((u?.role || "").toLowerCase() === "vendor") { setVendors([]); return; }
    try {
      const res = await getInvoiceVendors({ activeOnly: "true" });
      setVendors(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch invoice vendors:", err);
    }
  }, [getUserFromToken]);

  // Whether the current user may generate invoices (+ a vendor's allowed properties).
  const fetchEligibility = useCallback(async () => {
    try {
      const res = await getInvoiceEligibility();
      setEligibility(res || null);
    } catch {
      setEligibility(null);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setTableLoading(true);
    try {
      const params = {};
      if (filters.propertyId)   params.propertyId   = filters.propertyId;
      if (filters.buildingName) params.buildingName = filters.buildingName;
      if (filters.year)         params.year         = filters.year;
      if (filters.month)        params.month        = filters.month;
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.categoryId)   params.categoryId   = filters.categoryId;
      if (filters.vendorName)   params.vendorName   = filters.vendorName;
      if (filters.entered)      params.entered      = filters.entered;
      if (filters.referenceNo)  params.referenceNo  = filters.referenceNo;
      if (filters.unitNo)       params.unitNo       = filters.unitNo;
      const res = await getInvoices(params);
      setInvoices(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
      setInvoices([]);
    } finally {
      setTableLoading(false);
    }
  }, [filters]);

  const fetchStatusData = useCallback(async () => {
    try {
      const params = {};
      if (filters.propertyId)   params.propertyId   = filters.propertyId;
      if (filters.buildingName) params.buildingName = filters.buildingName;
      if (filters.year)         params.year         = filters.year;
      if (filters.month)        params.month        = filters.month;
      if (filters.categoryId)   params.categoryId   = filters.categoryId;
      if (filters.vendorName)   params.vendorName   = filters.vendorName;
      if (filters.entered)      params.entered      = filters.entered;
      if (filters.referenceNo)  params.referenceNo  = filters.referenceNo;
      if (filters.unitNo)       params.unitNo       = filters.unitNo;
      const res = await getInvoiceStatusCounts(params);
      setStatusData(res?.data || { counts: {}, totals: {} });
    } catch (err) {
      console.error("Failed to fetch invoice status data:", err);
      setStatusData({ counts: {}, totals: {} });
    }
  }, [filters]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => { fetchEligibility(); }, [fetchEligibility]);
  useEffect(() => {
    if (activeTab === TABS.INVOICES) {
      fetchInvoices();
      fetchStatusData();
    }
  }, [fetchInvoices, fetchStatusData, activeTab]);

  // Auto-select building when property has exactly one building
  useEffect(() => {
    if (!filters.propertyId) return;
    const selected = properties.find((p) => p._id === filters.propertyId);
    if (!selected) return;
    const valid = (selected.building || []).filter((b) => b?.buildingName);
    if (valid.length === 1) {
      setFilters((prev) => ({ ...prev, buildingName: valid[0].buildingName }));
    } else {
      const belongs = valid.some((b) => b.buildingName === filters.buildingName);
      if (!belongs) setFilters((prev) => ({ ...prev, buildingName: "" }));
    }
  }, [filters.propertyId, properties]); // eslint-disable-line

  // Our RD number lives in the numeric `invoiceNo` field, which the list
  // endpoint has no filter for — its `referenceNo` param matches the vendor's
  // own number instead. So this one is applied here, over the rows the server
  // already scoped and returned. `getAll` returns every matching line unpaged,
  // so matching locally sees the whole result set, not a page of it.
  const rdDigits = rdNoDraft.replace(/\D/g, "");
  const matchesRdNo = (inv) => {
    const n = String(inv?.invoiceNo ?? "").replace(/\D/g, "");
    return n !== "" && Number(n) === Number(rdDigits);
  };
  const visibleInvoices = rdDigits ? (invoices || []).filter(matchesRdNo) : invoices;

  const activeFilterCount = [
    filters.propertyId, filters.categoryId, filters.month, filters.year,
    filters.vendorName, filters.entered, filters.referenceNo, filters.unitNo, rdDigits,
    filters.status !== "all" ? filters.status : "",
  ].filter(Boolean).length;

  // Filters tucked inside the "More filters" accordion.
  const advancedFilterCount = [
    filters.categoryId, filters.month, filters.year, filters.vendorName,
    filters.entered, filters.referenceNo, filters.unitNo, rdDigits,
  ].filter(Boolean).length;

  // Company accounts and lead technical staff may manage the vendor catalog and approve.
  // Managers may still manage categories, but vendor add/remove stays with company + leads.
  const role = (user?.role || "").toLowerCase();
  const isVendor = role === "vendor";
  const isLead = role === "technical staff" && !!user?.lead;
  const isSimpleStaff = role === "technical staff" && !user?.lead;
  const canManageVendors = role === "company" || isLead;
  const canManageCategories = role === "company" || role === "manager" || isLead;
  const canReviewVendors = role === "company" || isLead;

  const propertyOptions = [
    { value: "", label: "All Properties" },
    ...(properties || [])
      .map((p) => ({ value: p._id, label: p.title || p.name || p._id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const buildingOptions = (() => {
    if (!filters.propertyId) return [{ value: "", label: "All Buildings" }];
    const prop = properties.find((p) => p._id === filters.propertyId);
    const buildings = (prop?.building || []).filter((b) => b?.buildingName);
    return [
      { value: "", label: "All Buildings" },
      ...buildings.map((b) => ({ value: b.buildingName, label: b.buildingName })),
    ];
  })();

  const hasSingleBuilding = (() => {
    if (!filters.propertyId) return false;
    const prop = properties.find((p) => p._id === filters.propertyId);
    return ((prop?.building || []).filter((b) => b?.buildingName)).length === 1;
  })();

  const allCategoryOptions = [{ value: "", label: "All Categories" }, ...categoryOptions];

  const vendorFilterOptions = [
    { value: "", label: "All Vendors" },
    ...(vendors || [])
      .map((v) => ({ value: v.name, label: v.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const tabStyle = (tab) => ({
    cursor: "pointer",
    padding: "9px 22px",
    fontWeight: 700,
    fontSize: "0.85rem",
    color: activeTab === tab ? "#4F46E5" : "#64748B",
    borderBottom: activeTab === tab ? "2px solid #4F46E5" : "2px solid transparent",
    background: "none",
    border: "none",
    display: "flex",
    alignItems: "center",
    gap: 6,
    transition: "all 0.15s ease",
  });

  const grandTotal = statusData.totals?.all || 0;
  const pendingTotal = statusData.totals?.Pending || 0;
  const approvedTotal = statusData.totals?.Approved || 0;
  const paidTotal = statusData.totals?.Paid || 0;

  // Simple technical staff (non-leads) have no access to the Invoices module.
  if (user && isSimpleStaff) {
    return (
      <div className="page-content">
        <MetaTags><title>Rentdigicare | Invoices</title></MetaTags>
        <div className="container-fluid">
          <Breadcrumbs title="Finance" breadcrumbItem="Invoices" />
          <div style={{
            background: "white", border: "1px solid #E2E8F0", borderRadius: 16,
            padding: "60px 24px", textAlign: "center", maxWidth: 560, margin: "40px auto",
            boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: 16, margin: "0 auto 16px",
              background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FeatherIcon icon="lock" size={28} color="#94A3B8" />
            </div>
            <h4 style={{ fontWeight: 800, color: "#0F172A" }}>Restricted Module</h4>
            <p style={{ color: "#64748B", fontSize: "0.9rem", marginTop: 6 }}>
              The Invoices module is available to technical <strong>leads</strong> only.
              Please contact your administrator if you need access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Invoices</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Finance" breadcrumbItem="Invoices" />

        {/* Tabs */}
        <div style={{
          display: "flex", alignItems: "center", marginBottom: 18,
          borderBottom: "1px solid #E2E8F0", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <button style={tabStyle(TABS.INVOICES)} onClick={() => setActiveTab(TABS.INVOICES)}>
              <FeatherIcon icon="file-text" size={14} /> {isVendor ? "My Invoices" : "Invoices"}
            </button>
            {!isVendor && canReviewVendors && (
              <button style={tabStyle(TABS.REVIEW)} onClick={() => setActiveTab(TABS.REVIEW)}>
                <FeatherIcon icon="check-square" size={14} /> Approvals
              </button>
            )}
            {!isVendor && (
              <>
                <button style={tabStyle(TABS.VENDORS)} onClick={() => setActiveTab(TABS.VENDORS)}>
                  <FeatherIcon icon="briefcase" size={14} /> Vendors
                </button>
                <button style={tabStyle(TABS.CATEGORIES)} onClick={() => setActiveTab(TABS.CATEGORIES)}>
                  <FeatherIcon icon="layers" size={14} /> Categories
                </button>
                <button style={tabStyle(TABS.AUDITORS)} onClick={() => setActiveTab(TABS.AUDITORS)}>
                  <FeatherIcon icon="shield" size={14} /> Auditors
                </button>
                <button style={tabStyle(TABS.SETTINGS)} onClick={() => setActiveTab(TABS.SETTINGS)}>
                  <FeatherIcon icon="calendar" size={14} /> Payment Dates
                </button>
              </>
            )}
          </div>
        </div>

        {activeTab === TABS.INVOICES && (
          <>
            {/* Summary Cards */}
            <div className="iv-summary-grid">
              <div className="iv-summary-card iv-summary-card--all">
                <div className="iv-summary-card__icon"><FeatherIcon icon="hash" size={16} /></div>
                <div>
                  <div className="iv-summary-card__label">Total Invoices</div>
                  <div className="iv-summary-card__value">{statusData.counts?.all || 0}</div>
                  <div className="iv-summary-card__sub">{formatCurrency(grandTotal)}</div>
                </div>
              </div>
              <div className="iv-summary-card iv-summary-card--outstanding">
                <div className="iv-summary-card__icon"><FeatherIcon icon="clock" size={16} /></div>
                <div>
                  <div className="iv-summary-card__label">Pending Approval</div>
                  <div className="iv-summary-card__value">{formatCurrency(pendingTotal)}</div>
                  <div className="iv-summary-card__sub">{statusData.counts?.Pending || 0} invoices</div>
                </div>
              </div>
              <div className="iv-summary-card iv-summary-card--approved">
                <div className="iv-summary-card__icon"><FeatherIcon icon="check" size={16} /></div>
                <div>
                  <div className="iv-summary-card__label">Approved</div>
                  <div className="iv-summary-card__value">{formatCurrency(approvedTotal)}</div>
                  <div className="iv-summary-card__sub">{statusData.counts?.Approved || 0} invoices</div>
                </div>
              </div>
              <div className="iv-summary-card iv-summary-card--paid">
                <div className="iv-summary-card__icon"><FeatherIcon icon="check-circle" size={16} /></div>
                <div>
                  <div className="iv-summary-card__label">Paid</div>
                  <div className="iv-summary-card__value">{formatCurrency(paidTotal)}</div>
                  <div className="iv-summary-card__sub">{statusData.counts?.Paid || 0} invoices</div>
                </div>
              </div>
              <div className="iv-summary-card iv-summary-card--entered">
                <div className="iv-summary-card__icon"><FeatherIcon icon="inbox" size={16} /></div>
                <div>
                  <div className="iv-summary-card__label">Approved · Not Entered</div>
                  <div className="iv-summary-card__value">{formatCurrency(statusData.totals?.approvedNotEntered || 0)}</div>
                  <div className="iv-summary-card__sub">{statusData.counts?.approvedNotEntered || 0} invoices</div>
                </div>
              </div>
            </div>

            {/* Status radio row */}
            <Form className="mb-3">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
                {INVOICE_STATUS_BUTTONS.map((s) => (
                  <FormGroup check inline key={s.value} style={{ marginBottom: 0 }}>
                    <Input
                      type="radio"
                      name="invStatus"
                      checked={filters.status === s.value}
                      onChange={() => setFilters((f) => ({ ...f, status: s.value }))}
                    />
                    <Label check style={{ color: s.color, fontWeight: 600, fontSize: "0.83rem", cursor: "pointer" }}>
                      <FeatherIcon icon={s.icon} size={14} style={{ marginRight: 4, verticalAlign: "middle" }} />
                      {s.label}
                      {statusData.counts?.[s.value] !== undefined && (
                        <span style={{
                          marginLeft: 5, background: "#F1F5F9", color: "#475569",
                          borderRadius: 20, padding: "1px 7px",
                          fontSize: "0.72rem", fontWeight: 700,
                        }}>
                          {statusData.counts[s.value]}
                        </span>
                      )}
                    </Label>
                  </FormGroup>
                ))}
              </div>
            </Form>

            {/* Filter bar — property + building stay on top; the rest collapse
                into an accordion. Hidden for vendors, who only ever see their
                own invoices. */}
            {!isVendor && (
            <div style={{
              background: "white", border: "1px solid #E2E8F0", borderRadius: 12,
              padding: "14px 18px", marginBottom: 16,
              boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
            }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginRight: 4 }}>
                  <FeatherIcon icon="map-pin" size={14} color="#4F46E5" />
                  <span style={{
                    fontSize: "0.78rem", fontWeight: 700, color: "#0F172A",
                    textTransform: "uppercase", letterSpacing: "0.5px",
                  }}>Property</span>
                </div>

                <div style={{ minWidth: 200 }}>
                  <Select
                    options={propertyOptions}
                    value={propertyOptions.find((o) => o.value === filters.propertyId) || propertyOptions[0]}
                    onChange={(opt) => setFilters((f) => ({ ...f, propertyId: opt?.value || "", buildingName: "" }))}
                    styles={selectStyles} placeholder="All Properties"
                    menuPortalTarget={document.body} menuPosition="fixed"
                  />
                </div>

                <div style={{ minWidth: 200 }}>
                  <Select
                    options={buildingOptions}
                    value={buildingOptions.find((o) => o.value === filters.buildingName) || buildingOptions[0]}
                    onChange={(opt) => setFilters((f) => ({ ...f, buildingName: opt?.value || "" }))}
                    styles={selectStyles} placeholder="All Buildings"
                    isDisabled={!filters.propertyId || hasSingleBuilding}
                    menuPortalTarget={document.body} menuPosition="fixed"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setAdvancedOpen((o) => !o)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    background: advancedOpen ? "#EEF2FF" : "#F8FAFC",
                    border: "1px solid #E2E8F0", borderRadius: 7,
                    color: "#4F46E5", fontSize: "0.76rem", fontWeight: 700,
                    padding: "6px 12px", cursor: "pointer",
                  }}
                >
                  <FeatherIcon icon="sliders" size={13} />
                  More filters
                  {advancedFilterCount > 0 && (
                    <span style={{
                      background: "#4F46E5", color: "white", borderRadius: 20,
                      padding: "1px 7px", fontSize: "0.68rem", fontWeight: 700,
                    }}>
                      {advancedFilterCount}
                    </span>
                  )}
                  <FeatherIcon icon={advancedOpen ? "chevron-up" : "chevron-down"} size={14} />
                </button>

                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={() => { setFilters({ ...emptyFilters }); setRefNoDraft(""); setRdNoDraft(""); setUnitNoDraft(""); }}
                    style={{
                      background: "#FEF2F2", border: "1px solid #FECDD3", borderRadius: 7,
                      color: "#DC2626", fontSize: "0.75rem", fontWeight: 600,
                      padding: "5px 12px", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 5,
                    }}
                  >
                    <FeatherIcon icon="x" size={12} /> Clear
                  </button>
                )}
              </div>

              {advancedOpen && (
                <div style={{
                  display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
                  marginTop: 12, paddingTop: 12, borderTop: "1px dashed #E2E8F0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginRight: 4 }}>
                    <FeatherIcon icon="filter" size={14} color="#64748B" />
                    <span style={{
                      fontSize: "0.74rem", fontWeight: 700, color: "#64748B",
                      textTransform: "uppercase", letterSpacing: "0.5px",
                    }}>Refine</span>
                  </div>

                  <div style={{ minWidth: 160 }}>
                    <Select
                      options={allCategoryOptions}
                      value={allCategoryOptions.find((o) => o.value === filters.categoryId) || allCategoryOptions[0]}
                      onChange={(opt) => setFilters((f) => ({ ...f, categoryId: opt?.value || "" }))}
                      styles={selectStyles} placeholder="All Categories"
                      menuPortalTarget={document.body} menuPosition="fixed"
                    />
                  </div>

                  <div style={{ minWidth: 130 }}>
                    <Select
                      options={MONTH_OPTIONS}
                      value={MONTH_OPTIONS.find((m) => m.value === filters.month) || MONTH_OPTIONS[0]}
                      onChange={(opt) => setFilters((f) => ({ ...f, month: opt?.value || "" }))}
                      styles={selectStyles} placeholder="All Months"
                      menuPortalTarget={document.body} menuPosition="fixed"
                    />
                  </div>

                  <div style={{ minWidth: 120 }}>
                    <Select
                      options={YEAR_OPTIONS}
                      value={YEAR_OPTIONS.find((y) => y.value === filters.year) || YEAR_OPTIONS[0]}
                      onChange={(opt) => setFilters((f) => ({ ...f, year: opt?.value || "" }))}
                      styles={selectStyles} placeholder="All Years"
                      menuPortalTarget={document.body} menuPosition="fixed"
                    />
                  </div>

                  <div style={{ minWidth: 180 }}>
                    <Select
                      options={vendorFilterOptions}
                      value={vendorFilterOptions.find((o) => o.value === filters.vendorName) || vendorFilterOptions[0]}
                      onChange={(opt) => setFilters((f) => ({ ...f, vendorName: opt?.value || "" }))}
                      styles={selectStyles} placeholder="All Vendors"
                      isDisabled={vendorFilterOptions.length <= 1}
                      menuPortalTarget={document.body} menuPosition="fixed"
                    />
                  </div>

                  {/* Two different numbers, so both say which one they are —
                      the placeholder alone disappears as soon as you type. */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{
                      fontSize: "0.74rem", fontWeight: 700, color: "#64748B",
                      whiteSpace: "nowrap",
                    }}>Invoice No.</span>
                    <div style={{ minWidth: 120 }}>
                      <Input
                        value={rdNoDraft}
                        onChange={(e) => setRdNoDraft(e.target.value)}
                        placeholder="RD 101"
                        style={{ height: 34, fontSize: "0.8rem", borderColor: "#E2E8F0", borderRadius: 8 }}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{
                      fontSize: "0.74rem", fontWeight: 700, color: "#64748B",
                      whiteSpace: "nowrap",
                    }}>Vendor Ref</span>
                    <div style={{ minWidth: 170 }}>
                      <Input
                        value={refNoDraft}
                        onChange={(e) => setRefNoDraft(e.target.value)}
                        placeholder="Vendor's invoice #"
                        style={{ height: 34, fontSize: "0.8rem", borderColor: "#E2E8F0", borderRadius: 8 }}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{
                      fontSize: "0.74rem", fontWeight: 700, color: "#64748B",
                      whiteSpace: "nowrap",
                    }}>Unit No.</span>
                    <div style={{ minWidth: 110 }}>
                      <Input
                        value={unitNoDraft}
                        onChange={(e) => setUnitNoDraft(e.target.value)}
                        placeholder="e.g. 101"
                        aria-label="Filter by unit number"
                        style={{ height: 34, fontSize: "0.8rem", borderColor: "#E2E8F0", borderRadius: 8 }}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{
                      fontSize: "0.74rem", fontWeight: 700, color: "#64748B",
                      whiteSpace: "nowrap",
                    }}>Entered in Acct</span>
                    <div style={{ minWidth: 140 }}>
                      <Select
                        options={ENTERED_OPTIONS}
                        value={ENTERED_OPTIONS.find((o) => o.value === filters.entered) || ENTERED_OPTIONS[0]}
                        onChange={(opt) => setFilters((f) => ({ ...f, entered: opt?.value || "" }))}
                        styles={selectStyles} placeholder="All"
                        menuPortalTarget={document.body} menuPosition="fixed"
                      />
                    </div>
                  </div>

                  {/* This one is matched on the client, so the server-side
                      aggregates behind the cards never see it. Say so. */}
                  {!!rdDigits && (
                    <div style={{ flexBasis: "100%", fontSize: "0.72rem", color: "#64748B" }}>
                      Invoice No. narrows the list below only — the summary cards above still
                      cover every invoice matching the other filters.
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            <InvoicesTable
              invoices={visibleInvoices}
              loading={tableLoading}
              properties={properties}
              vendors={vendors}
              userRole={user?.role || ""}
              userId={user?.id || ""}
              userLead={!!user?.lead}
              vendorEligibility={eligibility}
              onRefresh={() => { fetchInvoices(); fetchStatusData(); }}
              filters={filters}
            />
          </>
        )}

        {activeTab === TABS.REVIEW && canReviewVendors && (
          <InvoiceReviewQueue
            onAfterReview={() => { fetchInvoices(); fetchStatusData(); }}
          />
        )}

        {activeTab === TABS.VENDORS && (
          <InvoiceVendorsTable
            userRole={user?.role || ""}
            canManage={canManageVendors}
            onRefresh={fetchVendors}
          />
        )}

        {activeTab === TABS.CATEGORIES && (
          <InvoiceCategoriesTable
            userRole={user?.role || ""}
            canManage={canManageCategories}
            onRefresh={() => { fetchCategories(); fetchVendors(); }}
          />
        )}

        {activeTab === TABS.AUDITORS && (
          <InvoiceAuditorsTable userRole={user?.role || ""} />
        )}

        {activeTab === TABS.SETTINGS && (
          <InvoicePaymentSettings userRole={user?.role || ""} />
        )}
      </div>
    </div>
  );
};

export { Invoice };
